import React, { FC, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Layer } from 'react-konva';
import { useLatest } from 'src/hooks/useLatest';
import HorizontalGuideHandle from './horizontalGuideHandle';
import VerticalHandle from './verticalHandle';
import { defaultMarkers } from './defaultMarkers';
import type { GarmentMarkersType } from 'src/types/decorationSpecs';

type Point = { x: number; y: number };

interface GarmentMarkerOverlayProps {
    /** `undefined` while the background hasn't been calibrated yet — deliberately the *raw*
     * Redux value, not a computed default, so this prop stays referentially stable across
     * re-renders whenever the underlying data hasn't actually changed (the container passes it
     * straight through for the same reason — see its own comment). This component derives a
     * default itself, from `backgroundWidth`/`backgroundHeight`, when it's `undefined`. */
    markers?: GarmentMarkersType;
    backgroundWidth?: number;
    backgroundHeight?: number;
    scale: number;
    offset: Point;
    canvasWidth: number;
    cursorChange: (cursor: string) => void;
    onChange: (markers: GarmentMarkersType) => void;
}

/**
 * The 5 garment-landmark markers, rendered directly on the main canvas (inside the Konva Stage).
 * Dragging updates local state immediately for responsiveness, committing to Redux via `onChange`
 * upon drag end. When not dragging, props synchronize to state so undo/external changes reflect correctly.
 */
const GarmentMarkerOverlay: FC<GarmentMarkerOverlayProps> = ({
    markers,
    backgroundWidth,
    backgroundHeight,
    scale,
    offset,
    canvasWidth,
    cursorChange,
    onChange,
}) => {
    // Memoized so this only produces a new object when `markers`/`backgroundWidth`/
    // `backgroundHeight` actually change, not on every render — a fresh object every render here
    // would otherwise re-fire the sync effect below on every unrelated re-render of this component.
    const initialMarkers = useMemo(
        () =>
            markers ??
            (backgroundWidth !== undefined && backgroundHeight !== undefined
                ? defaultMarkers({ width: backgroundWidth, height: backgroundHeight })
                : undefined),
        [markers, backgroundWidth, backgroundHeight],
    );

    const [localMarkers, setLocalMarkers] = useState<GarmentMarkersType | undefined>(initialMarkers);
    const draggingRef = useRef(false);
    const markersRef = useLatest(localMarkers);

    useEffect(() => {
        if (!draggingRef.current) {
            setLocalMarkers(initialMarkers);
        }
    }, [initialMarkers]);

    const onDragMove = useCallback(
        (field: keyof GarmentMarkersType) => (value: number) => {
            let clampedValue = value;
            if (field === 'leftX' || field === 'rightX') {
                if (backgroundWidth !== undefined && backgroundWidth > 0) {
                    clampedValue = Math.max(0, Math.min(backgroundWidth, value));
                }
            } else if (backgroundHeight !== undefined && backgroundHeight > 0) {
                clampedValue = Math.max(0, Math.min(backgroundHeight, value));
            }

            draggingRef.current = true;
            setLocalMarkers((prev) => (prev ? { ...prev, [field]: clampedValue } : prev));
        },
        [backgroundWidth, backgroundHeight],
    );

    const onDragEnd = useCallback(() => {
        if (markersRef.current) {
            onChange(markersRef.current);
        }
        draggingRef.current = false;
    }, [onChange, markersRef]);

    if (!localMarkers) {
        return null;
    }

    return (
        <Layer>
            <HorizontalGuideHandle
                y={localMarkers.shoulderTopY}
                label='Shoulder top'
                color='#ff8300'
                scale={scale}
                offset={offset}
                canvasWidth={canvasWidth}
                cursorChange={cursorChange}
                onDragMove={onDragMove('shoulderTopY')}
                onDragEnd={onDragEnd}
            />
            <HorizontalGuideHandle
                y={localMarkers.collarBottomY}
                label='Collar bottom'
                color='#00b5e2'
                scale={scale}
                offset={offset}
                canvasWidth={canvasWidth}
                cursorChange={cursorChange}
                onDragMove={onDragMove('collarBottomY')}
                onDragEnd={onDragEnd}
            />
            <HorizontalGuideHandle
                y={localMarkers.measureY}
                label='Body width measured here'
                color='#7dc242'
                scale={scale}
                offset={offset}
                canvasWidth={canvasWidth}
                cursorChange={cursorChange}
                onDragMove={onDragMove('measureY')}
                onDragEnd={onDragEnd}
            />
            <HorizontalGuideHandle
                y={localMarkers.garmentBottomY}
                label='Garment bottom'
                color='#8e44ad'
                scale={scale}
                offset={offset}
                canvasWidth={canvasWidth}
                cursorChange={cursorChange}
                onDragMove={onDragMove('garmentBottomY')}
                onDragEnd={onDragEnd}
            />
            <VerticalHandle
                x={localMarkers.leftX}
                y={localMarkers.measureY}
                scale={scale}
                offset={offset}
                cursorChange={cursorChange}
                onDragMove={onDragMove('leftX')}
                onDragEnd={onDragEnd}
            />
            <VerticalHandle
                x={localMarkers.rightX}
                y={localMarkers.measureY}
                scale={scale}
                offset={offset}
                cursorChange={cursorChange}
                onDragMove={onDragMove('rightX')}
                onDragEnd={onDragEnd}
            />
        </Layer>
    );
};

export default GarmentMarkerOverlay;
